<template>
  <layout hide-search-cta inverse subheader>
    <template #subheader="subheaderProps">
      <hotel-sections-navigation
        :inverse="subheaderProps.inverse"
        :sections="submenuSections" />
    </template>

    <div class="Hotel no-overflow-x">
      <hotel-hero
        :alt="hotel.heroImage.alt"
        :breadcrumb-items="breadcrumbItems"
        :city-label="hotel.address.city"
        :country-label="hotel.address.country"
        :experience-categories="experienceCategories"
        :hotel-brand-code="hotel.brand"
        :hotel-image="hotel.heroImage"
        :hotel-video="hotel.heroVideo"
        :hotel-name="hotel.name"
        :hotel-slug="hotel.slug"
        :number-of-star="hotel.rating.stars"
        :rating="hotel.reviews.average"
        :reviews="hotel.reviews.count"
        variant="inverse" />

      <section
        ref="searchBarSection"
        :aria-label="$t('hotel.searchBlock.searchCtaLabel')">
        <div class="Layout Hotel-search">
          <grid-item md="12" sm="4">
            <ai-typo as="h2" class="Hotel-search-title" variant="heading-02">
              {{ $t('hotel.searchBlock.title') }}
            </ai-typo>
          </grid-item>

          <grid-item md="12" sm="4">
            <hotel-booking-bar
              ref="bookingBar"
              v-model:dates="dates"
              v-model:rooms-and-guests="roomsAndGuests"
              :bottom="isSearchbarBottom"
              :class="{
                'Hotel-bookingBar--sticky': isSearchbarSticky,
                'Hotel-bookingBar--bottom': isSearchbarBottom,
              }"
              :destination-id="destinationId"
              :minimum-stay-duration="hotel.minimumStayDuration"
              :name="hotelName"
              :ridcode="ridcode"
              :sticky="isSearchbarSticky"
              :children-max-age="apiHotel?.maxChildAge || undefined"
              class="Hotel-bookingBar"
              @best-offer-event="sendEcommerceEvent" />
          </grid-item>
        </div>
      </section>

      <hotel-welcome
        v-if="!pageConfiguration.hideIntroduction"
        id="introduction"
        :check-in-label="hotel.checkInHour"
        :check-out-label="hotel.checkOutHour"
        :description="hotel.description"
        :images="intro.images"
        :name="hotel.name"
        :map="hotel.map" />

      <hotel-specification
        v-if="!pageConfiguration.hideServices && facilities.length"
        :specification="facilities"
        variant="inverse" />

      <hotel-rooms
        id="rooms"
        :availabilities="availabilities"
        :dates="dates"
        :ridcode="ridcode"
        :rooms="rooms"
        :rooms-and-guests="roomsAndGuests" />

      <hotel-entertainments
        v-if="!pageConfiguration.hideEntertainments && entertainments.length"
        id="entertainment"
        :entertainments="entertainments"
        :location="hotel.name"
        :title="$t(`hotel.events.title`)" />

      <hotel-bars-and-restaurants
        v-if="
          !pageConfiguration.hideFoodsAndBeverages && hotelFacilities.length
        "
        id="food-and-drinks"
        :aria-label="
          $t('accessibility.description.hotelBarsAndRestaurantsCarousel')
        "
        :sections="hotelFacilities" />

      <hotel-kids-and-family
        v-if="!pageConfiguration.hideKidsAndFamilyOverview"
        id="family"
        :description="hotel.kfDescription"
        :images="kidsAndFamily.images" />

      <hotel-kids-and-family-activities
        v-if="
          !pageConfiguration.hideKidsAndFamilyActivity && kidsActivities?.length
        "
        :cards="kidsActivities" />

      <hotel-sports-overview
        v-if="
          sportsOverview.images?.length && !pageConfiguration.hideSportsOverview
        "
        id="sports"
        :description="hotel.soDescription"
        :images="sportsOverview.images" />

      <hotel-sports-and-fitness-activities
        v-if="
          !pageConfiguration.hideSportsActivities && sportActivities?.length
        "
        :cards="sportActivities" />

      <hotel-spa-activities
        v-if="!pageConfiguration.hideSpa && spaActivities?.length"
        id="wellness"
        :description="hotel.spaDescription"
        :spa-activities="spaActivities" />

      <hotel-offers-and-packages
        v-if="!pageConfiguration.hideOffer && offers?.length"
        id="offers"
        :all-offers-link="hotel.opSeeAllOffersLink"
        :description="hotel.opDescription"
        :offers-and-packages="offers" />

      <hotel-private-events
        v-if="!pageConfiguration.hideMeetingAndEvent && events.length"
        :private-events="events"
        :title="$t(`hotel.events.privateEventsTitle`)" />

      <hotel-map
        id="location"
        :address="address"
        :contact="contact"
        :hotel-name="hotel.name"
        :surroundings="surroundings" />

      <div
        v-if="!pageConfiguration.hideReview"
        id="client-reviews"
        ref="clientReviewsSection">
        <client-reviews-skeleton v-if="isLoading && reviews.length === 0" />
        <hotel-client-reviews
          v-if="reviews.length != 0"
          :all-reviews-load="allReviewsLoad"
          :hotel-id="ridcode"
          :rating="hotel.reviews.average"
          :reviews="reviews"
          :reviews-count="hotel.reviews.count"
          @load-more="reviewsLoadMore" />
      </div>

      <hotel-experiences
        v-if="!pageConfiguration.hide7Pillars && experiences.experiences.length"
        :experiences="experiences.experiences" />

      <hotel-loyalty-banner
        :loyalty="hotel.loyaltyProgram"
        @become-member-click="sendCtaEnrollEvent" />
    </div>
  </layout>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { differenceInCalendarDays } from 'date-fns';
import type { Thing, WithContext } from 'schema-dts';

import {
  UnconfiguredGuests,
  unserializeRemainingGuests,
} from '~/domains/booking';
import { useBookingStore } from '~/domains/booking/store';
import { useSearchStore } from '~/domains/search';
import {
  Accommodation,
  AddressFragment,
  AvailabilityRoom,
  ContactFragment,
  Facility,
  FacilitySection,
  HotelEntertainment,
  HotelEventFragment,
  HotelKidActivityFragment,
  HotelSpaActivity,
  HotelSportActivityFragment,
  Maybe,
  SurroundingsFragment,
  useHotelBySlugAsyncData,
  useHotelRoomsAvailabilitiesAsyncData,
} from '~/domains/graphql';
import {
  useHotelExperiences,
  useHotelFacilities,
  useHotelIntro,
  useHotelKidsAndFamily,
  useHotelOffers,
  useHotelOffersAndPackages,
  useHotelPageConfiguration,
  useHotelReviews,
  useHotelSearchBarValues,
  useHotelSportsOverview,
} from '~/domains/hotel';
import { AmenitiesMapping } from '~/domains/ux/atoms/AiIcon/constants';
import { ExperienceCategories } from '~/domains/ux/molecules/AiHero/AiHero.vue';
import type { AiSpecification } from '~/domains/ux/organisms/AiSpecification/types';
import {
  ecommerce,
  event,
  extractDateFromApiString,
  extractHotelFacilityStructuredData,
  extractHotelRoomStructuredData,
  extractHotelSpaActivityStructuredData,
  extractHotelStructuredData,
  formatDate,
  getRawBackendUrl,
  pageView,
} from '~/helpers';

const route = useRoute();
const localePath = useLocalePath();
const config = useRuntimeConfig();
const bookingStore = useBookingStore();
const searchStore = useSearchStore();

const { t } = useI18n();

const baseUrl = computed(() => config.public.baseUrl);
const slug = computed(() => route.params.hotelSlug.toString() ?? '');

const { data, error } = await useHotelBySlugAsyncData({ slug: slug.value });
useAsyncDataError(error, 'hotel-slug');

useSeoMetadata({
  metadata: data.value?.hotelBySlug?.seo,
  fallback: {
    title: t('seo.hotel.title', {
      name: data.value?.hotelBySlug?.name,
      city: data.value?.hotelBySlug?.address?.city,
    }),
    description:
      t('seo.hotel.description', {
        city: data.value?.hotelBySlug?.address?.city,
      }) + (data.value?.hotelBySlug?.description || ''),
    ogImage: data.value?.hotelBySlug?.heroImage,
  },
});

definePageMeta({
  key: 'hotel',
  async validate(routeToValidate) {
    const currentSlug = routeToValidate.params.hotelSlug;
    const isPreview = routeToValidate.query.draft === 'true';
    const serverUri = getRawBackendUrl();

    try {
      const response = await axios.get(`${serverUri}/hotels/by-slug`, {
        headers: {
          'x-preview': isPreview,
        },
        params: { slug: currentSlug },
      });

      return response.data;
    } catch (err) {
      return false;
    }
  },
});

const apiHotel = computed(() => data.value?.hotelBySlug);
// eslint-disable-next-line sonarjs/cognitive-complexity
const hotel = computed(() => ({
  address: {
    city: apiHotel.value?.address?.city ?? '',
    country: apiHotel.value?.address?.country ?? '',
  },
  brand: apiHotel.value?.brand ?? '',
  checkInHour: apiHotel.value?.checkInHour ?? '',
  checkOutHour: apiHotel.value?.checkOutHour ?? '',
  description: apiHotel.value?.description ?? '',
  expediaId: apiHotel.value?.expediaId ?? '',
  heroImage: {
    alt: apiHotel.value?.heroImage?.alt ?? '',
    id: apiHotel.value?.heroImage?.id ?? '',
    src: apiHotel.value?.heroImage?.src ?? '',
    srcSet: apiHotel.value?.heroImage?.srcSet ?? '',
  },
  heroVideo: {
    alt: apiHotel.value?.heroVideo?.alt ?? '',
    id: apiHotel.value?.heroVideo?.id ?? '',
    src: apiHotel.value?.heroVideo?.src ?? '',
    srcSet: apiHotel.value?.heroVideo?.srcSet ?? '',
  },
  kfDescription: apiHotel.value?.kfDescription ?? '',
  id: apiHotel.value?.id ?? '',
  loyaltyProgram: apiHotel.value?.loyaltyProgram ?? undefined,
  map: apiHotel.value?.map ?? '',
  minimumStayDuration: apiHotel.value?.minimumStayDuration ?? undefined,
  name: apiHotel.value?.name ?? '',
  opDescription: apiHotel.value?.opDescription ?? '',
  opSeeAllOffersLink: apiHotel.value?.opSeeAllOffersLink ?? '',
  rating: {
    stars: apiHotel.value?.rating?.stars ?? 0,
  },
  reviews: {
    average: apiHotel.value?.reviews?.average ?? undefined,
    count: apiHotel.value?.reviews?.count ?? undefined,
  },
  slug: apiHotel.value?.slug ?? '',
  soDescription: apiHotel.value?.soDescription ?? '',
  spaDescription: apiHotel.value?.spaDescription ?? '',
}));
const ridcode = computed(() => hotel.value.id);

const hotelName = computed(() => hotel.value.name);
const destinationId = computed(() => hotel.value.expediaId);

const hotelSearchBarValues = useHotelSearchBarValues();
const dates = ref<Date[]>([]);
const roomsAndGuests = ref<UnconfiguredGuests[]>([]);

if (route.query.dateIn && route.query.nights) {
  const dateIn = extractDateFromApiString(route.query.dateIn);
  const dateOut = new Date(dateIn);
  dateOut.setDate(dateOut.getDate() + Number(route.query.nights));

  dates.value = [dateIn, dateOut];
} else if (hotelSearchBarValues.dates.value?.length) {
  dates.value = hotelSearchBarValues.dates.value;
}

if (route.query.remainingGuests) {
  roomsAndGuests.value = unserializeRemainingGuests(route.query);
} else if (hotelSearchBarValues.roomsAndGuests.value?.length) {
  roomsAndGuests.value = hotelSearchBarValues.roomsAndGuests.value;
}

const { data: roomAvailabilities } = useHotelRoomsAvailabilitiesAsyncData({
  adults: roomsAndGuests.value[0]?.adults ?? 2,
  dateIn: formatDate(dates.value[0], 'yyyy-MM-dd'),
  id: ridcode.value,
  nights: Math.abs(
    differenceInCalendarDays(dates.value[0] ?? 0, dates.value[1] ?? 0),
  ),
  childrenAges: roomsAndGuests.value[0]?.childrenAges ?? [],
});

const availabilities = computed<AvailabilityRoom[]>(
  () =>
    (roomAvailabilities.value
      ?.hotelRoomsAvailabilities as AvailabilityRoom[]) ?? [],
);

const pageConfiguration = useHotelPageConfiguration(apiHotel);

const entertainments = computed(
  () => (apiHotel.value?.entertainments ?? []) as HotelEntertainment[],
);

const events = computed(
  () => (apiHotel.value?.events ?? []) as HotelEventFragment[],
);
const address = computed(
  () => (apiHotel.value?.address ?? undefined) as AddressFragment,
);
const contact = computed(
  () => (apiHotel.value?.contact ?? undefined) as ContactFragment,
);
const surroundings = computed(
  () => (apiHotel.value?.surroundings ?? undefined) as SurroundingsFragment,
);

const sportActivities = computed(
  () => (apiHotel.value?.sportActivities ?? []) as HotelSportActivityFragment[],
);

const kidsActivities = computed(
  () => (apiHotel.value?.kidsActivities ?? []) as HotelKidActivityFragment[],
);

const facilities = computed(
  () =>
    (apiHotel.value?.facilities
      ?.map(({ id, title }) => {
        const amenityIconName =
          AmenitiesMapping[id] ?? AmenitiesMapping.DEFAULT;

        return {
          id,
          title,
          icon: amenityIconName,
        };
      })
      .filter(amenity => Boolean(amenity.title)) ?? []) as AiSpecification[],
);

const spaActivities = computed(
  () => (apiHotel.value?.spaActivities ?? []) as HotelSpaActivity[],
);

const experienceCategories = computed(() => {
  if (
    !apiHotel.value?.experienceCategories ||
    apiHotel.value?.experienceCategories.length === 0
  ) {
    return [];
  }

  return apiHotel.value?.experienceCategories.map(item => ({
    title: item?.title ?? '',
    url: item?.logo?.src,
  })) as ExperienceCategories[];
});

const breadcrumbItems = [
  {
    label: t('navigation.page.home'),
    route: localePath({ name: 'index' }),
  },
  {
    label: apiHotel.value?.name ?? '',
    route: localePath({
      name: 'hotel-hotelSlug',
      params: { hotelSlug: slug.value },
    }),
  },
];

// Experiences
const experiences = useHotelExperiences(apiHotel);

// Intro block
const intro = useHotelIntro(apiHotel);

// Room block
const rooms = computed<Accommodation[] | undefined>(
  () => (apiHotel.value?.rooms as Accommodation[]) ?? undefined,
);

// Kids & family block
const kidsAndFamily = useHotelKidsAndFamily(apiHotel);

// Sports overview block
const sportsOverview = useHotelSportsOverview(apiHotel);

// Hotel facilities
const hotelFacilities = useHotelFacilities(apiHotel);

// Offers and package
const offersAndPackages = useHotelOffersAndPackages(apiHotel);

const hotelOffers = useHotelOffers(hotel);

const offers = computed(() => [
  ...hotelOffers.value,
  ...offersAndPackages.value,
]);

// reviews
const {
  allReviewsLoad,
  loadMore: reviewsLoadMore,
  reviews,
  size: reviewsSize,
  isLoading,
} = useHotelReviews(ridcode.value);

const clientReviewsSection = ref<HTMLElement>();

watchEffect(() => {
  const firstLoadOfReviews = reviews.value.length <= reviewsSize.value;
  const hashPresent = route.hash === '#client-reviews';

  if (firstLoadOfReviews && hashPresent) {
    clientReviewsSection.value?.scrollIntoView({
      behavior: 'smooth',
    });
  }
});

pageView({
  page_category: 'hotel details page',
  page_name: 'hotel::hotelpage',
  page_sub_category_level_1: 'hotel',
  page_sub_category_level_2: 'hotelpage',
});

function sendEcommerceEvent(values: { price: number; currency: string }) {
  const dataEcommerceEvent = searchStore.ecommerceEvent;
  ecommerce(
    'view_item',
    {
      currency: bookingStore.currentCurrency,
      ...dataEcommerceEvent,
      items: [
        {
          item_brand: apiHotel.value?.brand ?? '',
          item_category: 'room',
          item_category4: apiHotel.value?.address?.zipCode ?? '',
          item_id: apiHotel.value?.id,
          item_name: apiHotel.value?.name ?? '',
          location_id: apiHotel.value?.address?.countryCode ?? '',
          price: values.price,
          currency: values.currency,
        },
      ],
    },
    'Ecommerce - Item Detail View',
  );
}

useJsonld(() => {
  const jsonld = [] as WithContext<Thing>[];

  if (apiHotel.value) {
    jsonld.push(
      extractHotelStructuredData(apiHotel.value, baseUrl.value, localePath),
    );
  }

  if (rooms.value) {
    rooms.value.forEach(room => {
      jsonld.push(extractHotelRoomStructuredData(room));
    });
  }

  if (hotelFacilities.value && hotelFacilities.value.length > 0) {
    hotelFacilities.value.forEach((facilitySection: FacilitySection) => {
      facilitySection.entries.forEach((facility: Maybe<Facility>) => {
        if (!facility) {
          return;
        }

        jsonld.push(
          extractHotelFacilityStructuredData(facility, facilitySection.title),
        );
      });
    });
  }

  if (spaActivities.value && spaActivities.value.length > 0) {
    spaActivities.value.forEach((spaActivity: HotelSpaActivity) => {
      jsonld.push(extractHotelSpaActivityStructuredData(spaActivity));
    });
  }

  return jsonld;
});

const submenuSections = computed(() => {
  return [
    {
      id: 'introduction',
      label: t('hotel.navigation.sections.introduction'),
      visible: !pageConfiguration.value.hideIntroduction,
    },
    {
      id: 'rooms',
      label: t('hotel.navigation.sections.rooms'),
      visible: true,
    },
    {
      id: 'entertainment',
      label: t('hotel.navigation.sections.entertainment'),
      visible:
        !pageConfiguration.value.hideEntertainments &&
        entertainments.value.length !== 0,
    },
    {
      id: 'food-and-drinks',
      label: t('hotel.navigation.sections.foodAndDrinks'),
      visible:
        !pageConfiguration.value.hideFoodsAndBeverages &&
        hotelFacilities.value.length !== 0,
    },
    {
      id: 'family',
      label: t('hotel.navigation.sections.family'),
      visible:
        !pageConfiguration.value.hideKidsAndFamilyOverview &&
        kidsAndFamily.value.images &&
        kidsAndFamily.value.images.length > 0,
    },
    {
      id: 'sports',
      label: t('hotel.navigation.sections.sports'),
      visible:
        !!sportsOverview.value.images?.length &&
        !pageConfiguration.value.hideSportsOverview,
    },
    {
      id: 'wellness',
      label: t('hotel.navigation.sections.wellness'),
      visible:
        !pageConfiguration.value.hideSpa && !!spaActivities?.value?.length,
    },
    {
      id: 'offers',
      label: t('hotel.navigation.sections.offers'),
      visible:
        !pageConfiguration.value.hideOffer &&
        !!offersAndPackages?.value?.length,
    },
    {
      id: 'location',
      label: t('hotel.navigation.sections.location'),
      visible: true,
    },
    {
      id: 'client-reviews',
      label: t('hotel.navigation.sections.guestReviews'),
      visible:
        !pageConfiguration.value.hideReview && reviews.value.length !== 0,
    },
  ];
});

// Sticky search bar
const searchBarSection = ref<HTMLElement>();
const observer = ref<IntersectionObserver>();
const bookingBarObserver = ref<IntersectionObserver>();
const isSearchbarSticky = ref(false);
const isSearchbarBottom = ref(false);

onMounted(() => {
  if (hotelSearchBarValues.dates.value?.length > 0) {
    dates.value = hotelSearchBarValues.dates.value;
  }

  if (hotelSearchBarValues.roomsAndGuests.value?.length > 0) {
    roomsAndGuests.value = hotelSearchBarValues.roomsAndGuests.value;
  }

  const bottom = document.querySelector('footer');

  if (!searchBarSection.value) return;

  observer.value = new IntersectionObserver(entries => {
    for (const entry of entries) {
      isSearchbarSticky.value =
        !entry.isIntersecting && window.scrollY > window.screen.height / 2;
    }
  });

  observer.value.observe(searchBarSection.value);

  if (!bottom) return;

  bookingBarObserver.value = new IntersectionObserver(entries => {
    for (const entry of entries) {
      isSearchbarBottom.value = entry.isIntersecting;
    }
  });

  bookingStore.hotelDetails.city = apiHotel.value?.address?.city ?? '';
  bookingStore.hotelDetails.cityCode = apiHotel.value?.address?.zipCode ?? ' ';
  bookingStore.hotelDetails.country = apiHotel.value?.address?.country ?? '';
  bookingStore.hotelDetails.countryCode =
    apiHotel.value?.address?.countryCode ?? '';
  bookingStore.hotelDetails.hotelName = apiHotel.value?.name ?? '';
  bookingStore.hotelDetails.brandCode = apiHotel.value?.brand ?? '';

  bookingBarObserver.value.observe(bottom);

  if (apiHotel.value?.maxChildAge) {
    bookingStore.maxChildrenAge = apiHotel.value.maxChildAge;
  }
});

const sendCtaEnrollEvent = () => {
  event('cta_enroll', {
    event_name: 'cta_enroll',
    enroll_context: 'loyalty',
    pagename: 'hotel details page',
  });
};

onBeforeUnmount(() => {
  observer.value?.disconnect?.();
  bookingBarObserver.value?.disconnect?.();
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mq';

.Hotel-search {
  padding: constants.$inner-06 constants.$padding-02;
}

.Hotel-search-title {
  text-align: center;
  margin-bottom: constants.$margin-04;
}

.Hotel-bookingBar--sticky {
  @media (mq.$from-medium) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 110;
  }
}

.Hotel-bookingBar--bottom {
  &.Hotel-bookingBar--sticky {
    bottom: initial;
  }

  @media (mq.$from-medium) {
    top: 0;
  }
}

#client-reviews {
  scroll-margin-top: constants.$header;
}
</style>
